import { motion, useReducedMotion } from 'framer-motion';
import { StarIcon } from 'lucide-react';

import { cn } from '@nesso/ui/lib/utils';
import { Badge } from '@nesso/ui/primitives/badge';
import { TableCell, TableRow } from '@nesso/ui/primitives/table';

import type { Service } from '@/entities/service-catalog';
import { formatCurrencyFromCents } from '@/shared/lib/format-utils';

import { ServicesTableRowMenu } from './services-table-row/services-table-row-menu';

function DefaultBadge() {
    return (
        <Badge variant="info" className="gap-1">
            <StarIcon className="size-3 fill-current" />
            Default
        </Badge>
    );
}

type Props = {
    service: Service;
    highlighted?: boolean;
    onEdit: (service: Service) => void;
};

const MotionTableRow = motion.create(TableRow);

// A `<tr>` can't collapse its own height — row height follows the tallest cell (CSS 2.1 §17.5.3),
// so only the fade happens here. Each cell keeps zero vertical padding always; its `motion.div`
// (CELL_CONTENT_VARIANTS) owns the full height and padding instead, so the collapse animates down
// from the row's true height rather than from a padding/height class that already dropped to 0.
const ROW_VARIANTS = { exit: { opacity: 0 } } as const;

const ROW_EXIT_TRANSITION = { duration: 0.25, ease: 'easeInOut' } as const;

const CELL_CONTENT_VARIANTS = {
    exit: { height: 0, paddingTop: 0, paddingBottom: 0 },
} as const;

export function ServicesTableRow({ service, highlighted, onEdit }: Props) {
    const rate = formatCurrencyFromCents(service.rateCents);
    const prefersReducedMotion = useReducedMotion();

    return (
        <MotionTableRow
            variants={ROW_VARIANTS}
            exit={prefersReducedMotion ? undefined : 'exit'}
            transition={ROW_EXIT_TRANSITION}
            // The highlight colour (Figma 217:3493) fades back on this row's own `transition-colors`
            // once the page clears `highlightedCode` after `SERVICE_HIGHLIGHT_MS`.
            // The repeated `hover:` fill is what stops `TableRow`'s `hover:bg-muted/50` outranking it.
            className={cn(highlighted && 'bg-secondary-background hover:bg-secondary-background')}
        >
            <TableCell className="w-20 overflow-hidden py-0 font-semibold xs:w-26">
                <motion.div
                    variants={CELL_CONTENT_VARIANTS}
                    transition={ROW_EXIT_TRANSITION}
                    className="flex flex-col justify-center overflow-hidden py-2 xs:h-18"
                >
                    {service.code}
                </motion.div>
            </TableCell>
            {/* Below `xs` the row drops its fixed height and sizes to content (Figma phone
                frame), since a wrapped description or the default badge needs more than 72px. */}
            <TableCell className="overflow-hidden whitespace-normal py-0 xs:whitespace-nowrap">
                <motion.div
                    variants={CELL_CONTENT_VARIANTS}
                    transition={ROW_EXIT_TRANSITION}
                    className="flex flex-col justify-center overflow-hidden py-2 xs:h-18"
                >
                    {/* Below the `xs` breakpoint the Rate column drops, the Default
                        marker moves above the description, and duration and rate combine onto one
                        line — a different arrangement, not just a hidden column, so the two layouts
                        are separate blocks rather than one reflowed by CSS alone. */}
                    <div className="xs:hidden">
                        {service.isDefault && (
                            <div className="mb-1">
                                <DefaultBadge />
                            </div>
                        )}
                        <div className="font-semibold">{service.description}</div>
                        <div className="text-muted-foreground">{`${service.durationMinutes}m, ${rate}`}</div>
                    </div>
                    <div className="hidden xs:block">
                        <div className="flex items-center gap-2">
                            <span className="font-semibold">{service.description}</span>
                            {service.isDefault && <DefaultBadge />}
                        </div>
                        <div className="text-muted-foreground">{`${service.durationMinutes}m`}</div>
                    </div>
                </motion.div>
            </TableCell>
            <TableCell className="hidden w-25 overflow-hidden py-0 text-right xs:table-cell">
                <motion.div
                    variants={CELL_CONTENT_VARIANTS}
                    transition={ROW_EXIT_TRANSITION}
                    className="flex h-18 flex-col justify-center overflow-hidden py-2"
                >
                    {rate}
                </motion.div>
            </TableCell>
            <TableCell className="w-12 overflow-hidden py-0 text-right">
                <motion.div
                    variants={CELL_CONTENT_VARIANTS}
                    transition={ROW_EXIT_TRANSITION}
                    className="flex flex-col justify-center overflow-hidden py-2 xs:h-18"
                >
                    <ServicesTableRowMenu service={service} onEdit={onEdit} />
                </motion.div>
            </TableCell>
        </MotionTableRow>
    );
}
